import { cn, formatDuration, formatTimeRange } from '@/lib/utils'
import { CATEGORY_MAP, QUALITY_LABELS, INTENSITY_LABELS } from '@/lib/constants'
import type { ActivityRecord } from '@/lib/api'
import { useRecords } from '@/contexts/record-context'
import { Trash2, AlertTriangle } from 'lucide-react'

interface TimelineProps {
  records: ActivityRecord[]
}

function getDetailSummary(record: ActivityRecord): string {
  if (!record.details) return ''
  const details = record.details as Record<string, string>
  const parts: string[] = []

  if (details.quality) parts.push(QUALITY_LABELS[details.quality] || details.quality)
  if (details.type) parts.push(details.type)
  if (details.intensity) parts.push(INTENSITY_LABELS[details.intensity] || details.intensity)
  if (details.bookName) parts.push(`《${details.bookName}》`)
  if (details.task) parts.push(details.task)
  if (details.drink) parts.push(details.drink)
  if (details.method) parts.push(details.method)
  if (details.note) parts.push(details.note)

  return parts.slice(0, 2).join(' · ')
}

export function Timeline({ records }: TimelineProps) {
  const { deleteRecord } = useRecords()

  if (records.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-sm">今天还没有记录</p>
        <p className="text-xs mt-1">点击下方类别按钮开始计时吧</p>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {records.map((record, index) => {
        const config = CATEGORY_MAP[record.category as keyof typeof CATEGORY_MAP]
        if (!config) return null
        const Icon = config.icon
        const summary = getDetailSummary(record)

        return (
          <div
            key={record.id}
            className={cn(
              'flex items-start gap-3 py-3 relative group',
              index < records.length - 1 && 'border-b border-border/50'
            )}
          >
            {/* Timeline node */}
            <div className="flex flex-col items-center pt-1">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: config.bgColor }}
              >
                <Icon className="h-4 w-4" style={{ color: config.color }} />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold" style={{ color: config.color }}>
                    {config.label}
                  </span>
                  {record.is_auto_closed && (
                    <span className="inline-flex items-center gap-0.5 text-xs text-amber-500">
                      <AlertTriangle className="h-3 w-3" />
                      异常
                    </span>
                  )}
                </div>
                <span className="text-sm font-medium tabular-nums">
                  {formatDuration(record.duration)}
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {formatTimeRange(record.start_time, record.end_time)}
              </div>
              {summary && (
                <div className="text-xs text-muted-foreground mt-1 truncate">{summary}</div>
              )}
            </div>

            {/* Delete button */}
            <button
              onClick={() => deleteRecord(record.id)}
              className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-destructive/10 text-destructive/60 hover:text-destructive transition-all shrink-0 mt-1"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
